import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, FileAudio, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOME } from "@/constants/testIds";

const ACCEPT = ".mp3,.aiff,.aif,.flac,.wav";

export default function UploadZone({ file, onFile, disabled, onBlocked }) {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);
  const { t } = useTranslation();

  const handleFiles = useCallback(
    (files) => {
      if (!files || !files.length) return;
      const f = files[0];
      onFile(f);
    },
    [onFile]
  );

  const openPicker = () => {
    if (disabled) return;
    // Auth gate · prevent guest uploads entirely. When `onBlocked` is
    // provided (e.g. Home passes `() => setShowLogin(true)`) we call it
    // BEFORE opening the native file picker so the login modal appears
    // before the user picks a file. If `onBlocked` returns truthy we
    // treat it as "handled, don't open the picker".
    if (onBlocked && onBlocked() === true) return;
    inputRef.current?.click();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    if (disabled) return;
    if (onBlocked && onBlocked() === true) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      data-testid={HOME.uploadDropzone}
      onDragOver={(e) => {
        e.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      onClick={openPicker}
      className={`card-elev cursor-pointer transition-all duration-200 border-dashed p-10 flex flex-col items-center justify-center gap-3 min-h-[220px] ${
        hover ? "border-[color:var(--brand)] bg-blue-500/5" : ""
      } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      style={{ borderStyle: "dashed", borderWidth: 1.5 }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid={HOME.uploadInput}
        onChange={(e) => handleFiles(e.target.files)}
      />
      {!file ? (
        <>
          <div className="w-14 h-14 rounded-full flex items-center justify-center bg-blue-500/10">
            <UploadCloud className="text-[color:var(--brand)]" size={28} />
          </div>
          <div className="text-white font-display font-semibold text-lg">{t("upload.drag_here")}</div>
          <div className="text-sm text-[color:var(--fg-secondary)]">
            {t("upload.or")} <span className="text-[color:var(--brand)]">{t("upload.click_to_select")}</span>
          </div>
          <div className="tiny-caps text-[10px] mt-2">{t("upload.formats")}</div>
        </>
      ) : (
        <div className="flex items-center gap-4 w-full">
          <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <FileAudio className="text-[color:var(--brand)]" size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium truncate">{file.name}</div>
            <div className="text-xs text-[color:var(--fg-secondary)] metric-value mt-0.5">
              {(file.size / (1024 * 1024)).toFixed(2)} MB
            </div>
          </div>
          <button
            className="p-2 rounded-md hover:bg-white/5 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onFile(null);
            }}
            data-testid={HOME.clearBtn}
            aria-label={t("upload.remove_file")}
          >
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
